import { useState } from "react";
import { SUPPORTING_TOOLS, CATEGORIES } from "../config/brand";
import { FadeUp, LineReveal, Eyebrow } from "../components/Reveal";

export const ProductGrid = () => {
  const [cat, setCat] = useState("all");

  return (
    <section
      id="whats-inside"
      className="scroll-mt-20 bg-blushcream/60"
      aria-label="The complete system"
      data-testid="product-showcase"
    >
      <div className="mx-auto max-w-[1280px] px-5 py-20 sm:px-8 lg:py-28">
        <div className="max-w-3xl">
          <FadeUp>
            <Eyebrow>What's inside</Eyebrow>
          </FadeUp>
          <LineReveal
            as="h2"
            lines={["And there's more than", "the two core systems."]}
            className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-plum sm:text-5xl"
          />
          <FadeUp delay={0.15}>
            <p className="mt-5 text-base leading-relaxed text-plumcharcoal/75 sm:text-lg">
              Seven additional tools help you simplify the everyday details that
              compete for your attention.
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.2}>
          <div
            className="mt-10 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="group"
            aria-label="Explore products by area of life"
            data-testid="product-explorer"
          >
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={cat === c.id}
                data-testid={`explorer-${c.id}`}
                onClick={() => setCat(c.id)}
                className={`shrink-0 rounded-xl border px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] transition-[background-color,color,border-color,transform] duration-200 ${
                  cat === c.id
                    ? "-translate-y-0.5 border-plum bg-plum text-ivory shadow-[0_8px_20px_rgba(90,48,73,0.25)]"
                    : "border-mauve/40 bg-warmwhite text-plumcharcoal/65 hover:border-rose/50 hover:text-plum"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </FadeUp>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SUPPORTING_TOOLS.map((p, i) => {
            const match = cat === "all" || p.cats.includes(cat);
            return (
              <FadeUp key={p.number} delay={(i % 3) * 0.07}>
                <article
                  data-testid={`product-card-${p.number}`}
                  className={`group flex h-full flex-col overflow-hidden rounded-3xl border bg-warmwhite transition-[box-shadow,transform,opacity,border-color,filter] duration-300 hover:-translate-y-1.5 hover:shadow-[0_22px_54px_rgba(90,48,73,0.12)] ${
                    match
                      ? "border-mauve/35 opacity-100 shadow-[0_8px_30px_rgba(90,48,73,0.04)] hover:border-rose/40"
                      : "scale-[0.985] border-mauve/25 opacity-40 grayscale-[35%]"
                  }`}
                >
                  <div className="relative overflow-hidden bg-blushcream/50">
                    <img
                      src={p.image}
                      alt={p.alt}
                      width={840}
                      height={1087}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover object-top transition-transform duration-700 ease-out group-hover:-translate-y-1 group-hover:scale-[1.04]"
                    />
                    <span className="absolute left-5 top-5 rounded-lg bg-plum/90 px-3 py-1.5 font-serif text-lg italic text-ivory backdrop-blur-sm transition-colors duration-300 group-hover:bg-rose">
                      {p.number}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-serif text-[1.35rem] font-semibold tracking-tight text-plum">
                      {p.name}
                      <span className="mt-2 block h-[2px] w-8 origin-left rounded-full bg-rose transition-transform duration-300 group-hover:w-16" />
                    </h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-plumcharcoal/70">
                      {p.description}
                    </p>
                    <p className="mt-auto pt-4 text-sm font-semibold text-rose transition-transform duration-300 group-hover:translate-x-1">
                      {p.benefit}
                    </p>
                  </div>
                </article>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
};
